import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useReadingSession } from '../voice/ReadingSession';
import { leadingWords } from '../voice/readingText';
import NarrationDock from '../voice/NarrationDock';
import { NewReadingIcon } from './spreads/DeckIcons';
import './ReadingDisplay.css';


// Private-use characters that bracket the sentence being spoken. They ride
// through the Markdown handling untouched and become a highlight span.
const SPEAKING_START = '\uE000';
const SPEAKING_END = '\uE001';
const SPEAKING_SPLIT = /([\uE000\uE001])/;

/** Plain text, with the spoken sentence (which may span runs and lines) lit. */
const renderRun = (text, marks) => {
  const nodes = [];
  for (const piece of text.split(SPEAKING_SPLIT)) {
    if (piece === SPEAKING_START) marks.on = true;
    else if (piece === SPEAKING_END) marks.on = false;
    else if (piece && marks.on) {
      // Keyed by sentence, so each new sentence fades its glow in
      nodes.push(<span key={`${marks.id}:${marks.key++}`} className="reading-text__now">{piece}</span>);
    } else if (piece) {
      nodes.push(piece);
    }
  }
  return nodes;
};

/**
 * The model answers in light Markdown — `**bold**` runs and `* ` bullets.
 * Those were being printed literally, asterisks and all. This renders the
 * two forms it actually uses, as React nodes rather than injected HTML.
 * `marks` carries the highlight state from one block to the next.
 */
const renderInline = (text, marks) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((chunk, i) =>
    chunk.startsWith('**') && chunk.endsWith('**') && chunk.length > 4
      ? <strong key={i}>{renderRun(chunk.slice(2, -2), marks)}</strong>
      : <Fragment key={i}>{renderRun(chunk, marks)}</Fragment>
  );

/** Groups the reading's lines into paragraphs and bullet lists. */
const toBlocks = (text) => {
  const blocks = [];

  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (!line) continue;

    const bullet = line.match(/^[*-]\s+(.*)$/);
    if (bullet) {
      const last = blocks[blocks.length - 1];
      if (last?.type === 'list') last.items.push(bullet[1]);
      else blocks.push({ type: 'list', items: [bullet[1]] });
    } else {
      blocks.push({ type: 'p', text: line });
    }
  }

  return blocks;
};

/**
 * Brackets a sentence's words with the speaking marks, leaving its leading
 * break or bullet and its trailing space outside so the Markdown still parses.
 */
const markSpeaking = (raw) => {
  const [, lead, body, trail] = raw.match(/^(\s*(?:[*-]\s+)?)([\s\S]*?)(\s*)$/);
  return body ? `${lead}${SPEAKING_START}${body}${SPEAKING_END}${trail}` : raw;
};

/**
 * The part of the reading the voice has reached, with sentence `active`
 * marked. A bold run cut mid-way is closed, so it renders bold rather than
 * as a stray `**`.
 */
const revealedText = (sentences, { spoken, words }, wordByWord, active) => {
  const reached = Math.min(spoken, sentences.length);
  if (!reached) return '';

  const marked = sentences.slice(0, reached)
    .map((sentence, i) => (i === active ? markSpeaking(sentence) : sentence));
  const text = marked.slice(0, -1).join('')
    + leadingWords(marked[reached - 1], wordByWord ? words : 1);

  return (text.split('**').length - 1) % 2 ? `${text}**` : text;
};

const prefersReducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * The full text of the reading, below the cards. It fills in as the voice
 * reaches each sentence but never pulls the page down with it; while it is
 * narrated the captions in the dock carry the words, and this is here for
 * anyone who scrolls down to read.
 */
function ReadingDisplay({ onNewReading }) {
  const { t } = useTranslation();
  const {
    sentences, status, error, narrated, narrator, voiceOn, toggleVoice, retry, readingRef
  } = useReadingSession();

  // Once everything has been said there is nothing left to highlight.
  const narrationOver = narrator.ended && status === 'done';
  const active = narrated && !narrationOver ? narrator.currentIndex : -1;

  // Reveal up to the furthest the voice has been, so going back re-reads
  // a passage without hiding what came after it.
  const text = narrated
    ? revealedText(sentences, narrator.reach, !prefersReducedMotion(), active)
    : sentences.join('');
  const marks = { id: active, on: false, key: 0 };

  return (
    <>
      <div className="reading-display" ref={readingRef}>
        <header className="reading-display__head">
          <h2>{t('reading.heading')}</h2>
          <div className="reading-display__rule" aria-hidden="true">
            <span className="reading-display__diamond" />
          </div>

          {narrator.supported && (
            <div className="reading-voice">
              <button
                type="button"
                className="reading-voice__btn"
                aria-pressed={voiceOn}
                onClick={toggleVoice}
              >
                {voiceOn ? t('voice.on') : t('voice.off')}
              </button>
            </div>
          )}
        </header>

        {!text.trim() && status !== 'error' && (
          <div className="loading">
            <p>{t('reading.loading')}</p>
          </div>
        )}

        {text.trim() && (
          <div className="ai-reading">
            <div className="reading-text" aria-live="off">
              {toBlocks(text).map((block, index) =>
                block.type === 'list' ? (
                  <ul key={index} className="reading-list">
                    {block.items.map((item, i) => (
                      <li key={i}>{renderInline(item, marks)}</li>
                    ))}
                  </ul>
                ) : (
                  <p key={index}>{renderInline(block.text, marks)}</p>
                )
              )}
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="error">
            <p>{t('reading.error')}</p>
            <p className="error-detail">{error}</p>
            <button
              type="button"
              className="btn btn-secondary reading-retry-btn"
              onClick={retry}
            >
              {t('reading.retry')}
            </button>
          </div>
        )}

        <button
          onClick={onNewReading}
          className="deck-btn deck-btn--primary new-reading-btn"
          aria-label={t('deck.startNewReading')}
          title={t('deck.startNewReading')}
        >
          <NewReadingIcon />
          <span className="deck-btn__label">{t('deck.newReading')}</span>
        </button>
      </div>

      <NarrationDock />
    </>
  );
}

export default ReadingDisplay;
